import Shared from '../shared.module.css';
import Styles from './styles.module.css';
import React, { useState, useRef, useEffect } from 'react';
import { deleteDoc, doc, getFirestore } from 'firebase/firestore';
import { BsThreeDots } from 'react-icons/bs';
import { IoCallOutline } from 'react-icons/io5';

type CouponCardProps = {
  // メニューの「編集する」
  onEdit: () => void;
  coupon: {
    id: string;
    docId: string;
    image: boolean;
    treatment: string;
    description: string;
    price: {
      before: string;
      after: string;
    };
    contact: {
      phone: string;
    };
  };
};

function formatPrice(value: string): string {
  const n = Number(value);
  return isNaN(n) ? value : `¥${n.toLocaleString('ja-JP')}`;
}

// 元 11.coupon/1.item/index.tsx の削除処理・表示項目をそのまま引き継ぎ、
// テーブル行からカードのレイアウトに変更しただけ。「編集」機能は元々存在
// しないため追加していない(削除メニューのみ)。画像は既存のimage/docIdが
// ある場合のみ表示し、新しい画像フィールドは追加していない。
// 割引率は既存のprice.before/afterから画面表示用に計算するだけで、
// Firestoreへは保存しない。
const CouponCard: React.FC<CouponCardProps> = ({ coupon, onEdit }) => {

  const db = getFirestore();

  const [ visible, setVisible ] = useState(false);
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const divRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        divRef.current && !divRef.current.contains(event.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(event.target as Node)
      ) {
        setVisible(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleButtonClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setVisible((prev) => !prev);
  };

  const deleteDocument = async () => {
    await deleteDoc(doc(db, 'coupons', coupon.id));
  };

  const imageUrl = `https://firebasestorage.googleapis.com/v0/b/trankllc-com.appspot.com/o/coupons%2F${coupon.docId}?alt=media`;

  const before = Number(coupon.price?.before);
  const after = Number(coupon.price?.after);
  const hasDiscount = !isNaN(before) && !isNaN(after) && before > after && before > 0;
  const discountPercent = hasDiscount ? Math.round((1 - after / before) * 100) : null;
  const showBefore = coupon.price?.before && coupon.price.before !== coupon.price.after;

  return (
    <div className={Shared.card}>
      {coupon.image && coupon.docId ? (
        <img className={Styles.image} src={imageUrl} alt={coupon.treatment} />
      ) : null}

      <h4 className={Styles.title}>{coupon.treatment}</h4>
      {coupon.description && <p className={Styles.description}>{coupon.description}</p>}

      <div className={Styles.priceBlock}>
        <span className={Styles.priceCaption}>学生料金</span>
        <div className={Styles.priceRow}>
          <span className={Styles.priceAfter}>
            {coupon.price?.after ? formatPrice(coupon.price.after) : '-'}
          </span>
          {showBefore && (
            <span className={Styles.priceBefore}>{formatPrice(coupon.price.before)}</span>
          )}
          {discountPercent !== null && (
            <span className={Styles.discountBadge}>{discountPercent}%OFF</span>
          )}
        </div>
      </div>

      {coupon.contact?.phone && (
        <div className={Styles.phoneRow}>
          <IoCallOutline className={Styles.phoneIcon} />
          <span>{coupon.contact.phone}</span>
        </div>
      )}

      <div className={Shared.cardActions}>
        <div className={Shared.cardMenuBtn} ref={buttonRef} onClick={handleButtonClick}>
          <BsThreeDots />
          {visible && (
            <div className={Shared.cardMenuPopover} ref={divRef}>
              <p onClick={() => { setVisible(false); onEdit(); }}>編集する</p>
              <p onClick={() => deleteDocument()}>削除する</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CouponCard;
